import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { AlertTriangle, BadgeCheck, Ban, Check, Landmark, Loader2 } from 'lucide-react';
import {
  apiErrorMessage, decideClosingAdvance, getClosingAdvance, updateClosingAdvance,
} from '../api/abfApi';
import type { ClosingAdvanceReadiness, DrawRequestRecord } from '../types/funding';
import { StatusChip } from '../components/StatusChip';
import { Button, Card, CardHead, Eyebrow, FieldLabel, INK, Input, Row, fieldBase } from '../onboarding/review/reviewStyles';
import { DateField } from '../../../components/shared/DateField';
import { fmtMoney } from '../presentation/format';

const Select = styled.select`
  ${fieldBase}
  option { background: #0f172a; color: ${INK.body}; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
  gap: 0.6rem;
  margin-top: 0.7rem;
`;

const Checks = styled.ul`
  display: grid;
  gap: 0.35rem;
  margin: 0.7rem 0 0;
  padding: 0;
  list-style: none;
`;

const CheckItem = styled.li<{ $ok: boolean }>`
  display: grid;
  grid-template-columns: 1rem minmax(0, 1fr);
  gap: 0.5rem;
  color: ${p => p.$ok ? INK.soft : '#fcd34d'};
  font-size: 0.74rem;
  line-height: 1.5;
  svg { margin-top: 0.15rem; color: ${p => p.$ok ? '#34d399' : '#fbbf24'}; }
`;

const Detail = styled.div`
  color: ${INK.muted};
  font-size: 0.72rem;
  line-height: 1.5;
  & + & { margin-top: 0.25rem; }
`;

const Error = styled.div`
  margin-top: 0.6rem;
  color: #fca5a5;
  font-size: 0.72rem;
`;

const money = (value: unknown, currency: string) => fmtMoney(value as number | string | null | undefined, currency || 'USD', 2, 'Not stated');

const day = (value: unknown) => value
  ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    .format(new Date(`${String(value).slice(0, 10)}T12:00:00`))
  : 'Not set';

const stamp = (value: unknown) => value
  ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    .format(new Date(String(value).replace(' ', 'T')))
  : '';

const evidenceOf = (request: DrawRequestRecord): Record<string, unknown> => {
  const raw = request.evidence_json;
  if (!raw) return {};
  if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return {}; } }
  return raw as Record<string, unknown>;
};

export interface ClosingAdvancePanelProps {
  facilityId: number;
  request: DrawRequestRecord;
  currency: string;
  canPrepare: boolean;
  canDecide: boolean;
  onChanged: (message: string) => Promise<void> | void;
}

/**
 * The funding a credit agreement states at closing. Activation stages the
 * proposal; this is where a maker records how it settles and on what authority,
 * and a checker decides. Nothing here issues a payment on its own.
 */
const ClosingAdvancePanel: React.FC<ClosingAdvancePanelProps> = ({
  facilityId, request, currency, canPrepare, canDecide, onChanged,
}) => {
  const [readiness, setReadiness] = useState<ClosingAdvanceReadiness | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const evidence = evidenceOf(request);
  const [settlement, setSettlement] = useState(request.settlement_mode || '');
  const [basis, setBasis] = useState(request.funding_basis || '');
  const [reference, setReference] = useState(request.funding_basis_reference || '');
  const [fundedOn, setFundedOn] = useState(request.funded_on ? String(request.funded_on).slice(0, 10) : '');
  const [settlementReference, setSettlementReference] = useState(String(evidence.settlementReference || ''));
  const [waiverAuthorizedBy, setWaiverAuthorizedBy] = useState(String(evidence.waiverAuthorizedBy || ''));

  const refresh = useCallback(async () => {
    try {
      setReadiness(await getClosingAdvance(facilityId, request.id));
    } catch (failure) {
      setError(apiErrorMessage(failure, 'The closing advance could not be read.'));
    }
  }, [facilityId, request.id]);

  useEffect(() => { void refresh(); }, [refresh]);

  const open = ['PROPOSED', 'PENDING_APPROVAL'].includes(String(request.status));
  const historical = settlement === 'HISTORICAL';
  const posted = String(request.status) === 'POSTED';

  const run = async (label: string, action: () => Promise<unknown>, message: string) => {
    setBusy(label);
    setError(null);
    try {
      await action();
      await refresh();
      await onChanged(message);
    } catch (failure) {
      setError(apiErrorMessage(failure, `The closing advance could not be ${label}.`));
    } finally {
      setBusy(null);
    }
  };

  const save = (event: React.FormEvent) => {
    event.preventDefault();
    void run('saved', () => updateClosingAdvance(facilityId, request.id, {
      settlementMode: settlement || null,
      fundingBasis: basis || null,
      fundingBasisReference: reference.trim() || null,
      fundedOn: historical && fundedOn ? fundedOn : null,
      evidence: {
        ...(settlementReference.trim() ? { settlementReference: settlementReference.trim() } : {}),
        ...(waiverAuthorizedBy.trim() ? { waiverAuthorizedBy: waiverAuthorizedBy.trim() } : {}),
      },
    }), 'Closing advance updated.');
  };

  const decide = (decision: 'APPROVE' | 'REJECT') => run(
    decision === 'APPROVE' ? 'approved' : 'rejected',
    () => decideClosingAdvance(facilityId, request.id, { decision }),
    decision === 'APPROVE'
      ? historical
        ? `Recorded ${money(request.amount, currency)} funded on ${day(request.funded_on || fundedOn)}.`
        : 'Closing advance approved. Release and confirm it to post the funding.'
      : 'Closing advance rejected.',
  );

  const outstanding = readiness?.outstanding ?? 0;
  const preparedBy = readiness?.preparedBy || null;

  return (
    <Card>
      <CardHead>
        <div>
          <Eyebrow>Closing advance</Eyebrow>
          <h2 style={{ marginTop: 4 }}>{money(request.amount, currency)} stated as drawn at closing</h2>
          <p>
            {request.proposal_source ? `From the agreement: ${request.proposal_source}. ` : ''}
            Closing date {day(request.requested_date)}. Activation stages this proposal; a checker decides.
          </p>
        </div>
        {posted
          ? <StatusChip tone="done" size="md" label={request.settlement_mode === 'HISTORICAL' ? 'Recorded' : 'Posted'} />
          : open
            ? <StatusChip tone={outstanding ? 'warning' : 'progress'} size="md" label={outstanding ? `${outstanding} to complete` : 'Ready to decide'} />
            : <StatusChip tone="progress" size="md" label={String(request.status) === 'RESERVED' ? 'Approved, awaiting release' : String(request.status) === 'RELEASED' ? 'Released, awaiting confirmation' : String(request.status).toLowerCase()} />}
      </CardHead>

      {posted && (
        <>
          <Detail><Landmark size={13} style={{ verticalAlign: '-2px', marginRight: 6 }} />
            {request.settlement_mode === 'HISTORICAL'
              ? `Funded ${day(request.funded_on)}, before onboarding. Recorded here ${stamp(request.recorded_at)} by ${request.approved_by}.`
              : `Posted with the funding lifecycle, confirmed by ${request.confirmed_by || request.approved_by}.`}
          </Detail>
          <Detail>Authorised by {request.funding_basis === 'BORROWING_BASE' ? 'the approved borrowing base'
            : request.funding_basis === 'WAIVER' ? 'a documented waiver' : 'a contractual provision'}
            {request.funding_basis_reference ? `: ${request.funding_basis_reference}` : ''}.</Detail>
        </>
      )}

      {!open && !posted && (
        <Detail>
          Approved by {request.approved_by}{request.approved_at ? `, ${stamp(request.approved_at)}` : ''}, on
          {' '}{request.funding_basis === 'BORROWING_BASE' ? 'the approved borrowing base'
            : request.funding_basis === 'WAIVER' ? 'a documented waiver' : 'a contractual provision'}
          {request.funding_basis_reference ? `: ${request.funding_basis_reference}` : ''}.
          {' '}Release and confirm it in the request panel below to post the funding.
        </Detail>
      )}

      {readiness && open && (
        <Checks>
          {readiness.checks.map(check => (
            <CheckItem key={check.key} $ok={check.satisfied}>
              {check.satisfied ? <Check size={13} aria-hidden="true" /> : <AlertTriangle size={13} aria-hidden="true" />}
              <span>{check.label}{check.detail ? `: ${check.detail}` : ''}</span>
            </CheckItem>
          ))}
        </Checks>
      )}

      {open && canPrepare && (
        <form onSubmit={save}>
          <Grid>
            <FieldLabel htmlFor="ca-settlement">
              Settlement
              <Select id="ca-settlement" value={settlement} onChange={event => setSettlement(event.target.value)}>
                <option value="">Choose…</option>
                <option value="PROSPECTIVE">Still to be funded</option>
                <option value="HISTORICAL">Already funded before onboarding</option>
              </Select>
            </FieldLabel>
            <FieldLabel htmlFor="ca-basis">
              Funding authority
              <Select id="ca-basis" value={basis} onChange={event => setBasis(event.target.value)}>
                <option value="">Choose…</option>
                <option value="BORROWING_BASE">Approved borrowing base</option>
                <option value="CONTRACTUAL_PROVISION">Contractual provision</option>
                <option value="WAIVER">Documented waiver</option>
              </Select>
            </FieldLabel>
            {basis && basis !== 'BORROWING_BASE' && (
              <FieldLabel htmlFor="ca-reference">
                {basis === 'WAIVER' ? 'Waiver document' : 'Provision'}
                <Input id="ca-reference" value={reference} onChange={event => setReference(event.target.value)}
                  placeholder={basis === 'WAIVER' ? 'Agent waiver letter, Sep 3, 2026' : 'Section 2.03(a), Initial Advance, p. 2'} />
              </FieldLabel>
            )}
            {basis === 'WAIVER' && (
              <FieldLabel htmlFor="ca-waiver-by">
                Authorised by
                <Input id="ca-waiver-by" value={waiverAuthorizedBy} onChange={event => setWaiverAuthorizedBy(event.target.value)}
                  placeholder="Who granted the waiver" />
              </FieldLabel>
            )}
            {historical && (
              <>
                <FieldLabel htmlFor="ca-funded-on">
                  Date the money moved
                  <DateField id="ca-funded-on" value={fundedOn} onChange={setFundedOn} />
                </FieldLabel>
                <FieldLabel htmlFor="ca-settlement-ref">
                  Evidence it funded
                  <Input id="ca-settlement-ref" value={settlementReference} onChange={event => setSettlementReference(event.target.value)}
                    placeholder="Wire confirmation or bank statement reference" />
                </FieldLabel>
              </>
            )}
          </Grid>
          {historical && <Detail style={{ marginTop: 8 }}>Recording keeps both dates: the day the money moved and the moment this platform learned of it. No payment is issued and no release is invented.</Detail>}
          {error && <Error role="alert">{error}</Error>}
          <Row style={{ marginTop: 10 }}>
            <Button type="submit" disabled={Boolean(busy)}>
              {busy === 'saved' ? <Loader2 size={13} className="spin" /> : <Check size={13} />} Save
            </Button>
          </Row>
        </form>
      )}

      {error && !canPrepare && <Error role="alert">{error}</Error>}

      {open && (
        <Row style={{ marginTop: 12 }}>
          <Button type="button" $variant="primary"
            disabled={Boolean(busy) || !canDecide || outstanding > 0}
            onClick={() => decide('APPROVE')}>
            {busy === 'approved' ? <Loader2 size={13} className="spin" /> : <BadgeCheck size={13} />}
            {historical ? 'Approve the recording' : 'Approve the advance'}
          </Button>
          <Button type="button" $variant="danger" disabled={Boolean(busy) || !canDecide} onClick={() => decide('REJECT')}>
            <Ban size={13} /> Reject
          </Button>
          {!canDecide && <Detail>An independent risk, operations or administrator user decides this.</Detail>}
          {canDecide && outstanding > 0 && <Detail>Complete the items above before deciding.</Detail>}
          {canDecide && outstanding === 0 && preparedBy && <Detail>Prepared by {preparedBy}; four-eyes means a different person decides.</Detail>}
        </Row>
      )}
      {!open && error && <Error role="alert">{error}</Error>}
    </Card>
  );
};

export default ClosingAdvancePanel;
